import { Inject, Injectable } from '@angular/core';

import { IS_SERVER } from '@core/config/tokens';
import { SPS } from '@core/config';

import { environment } from '../../../environments/environment';
import { TSpsEnvironmentKey } from '@interfaces/sps-environment';
import { SpsLogger } from '@lib/utils';

interface SpsEnvironment {
  production: boolean;
  key: TSpsEnvironmentKey;
  host: string;
  apiHost: string;
  eloquaApiUrl: string;
}

interface EnvironmentConfig {
  isProd: boolean;
  isDev: boolean;
  isServer: boolean;
  host: string;
  apiHost: string;
  eloquaApiUrl: string;
}

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  static environment: SpsEnvironment = environment as SpsEnvironment;

  static get host(): string {
    return environment.host;
  }

  static get apiHost(): string {
    return environment.apiHost;
  }

  static get cmsHost(): string {
    if (['prod-preview', 'prod-live'].includes(this.environment.key)) {
      return SPS.CMS_PROD_URL;
    }

    return this.apiHost;
  }

  static get isProdLive(): boolean {
    return environment.key === 'PROD_LIVE';
  }

  static get isSafari(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }

    let isSafari: boolean;

    try {
      if ('userAgentData' in navigator) {
        isSafari = (navigator['userAgentData']['brands'] || [])
          .map((entry: { brand: string }) => entry.brand?.toLowerCase() ?? '')
          .join('')
          .includes('apple');
      } else {
        isSafari = (navigator?.vendor || '').toLowerCase().includes('apple');
      }
    } catch (e) {
      SpsLogger.warn(`[ENVIRONMENT SERVICE] error checking for safari: ${e}`);
      isSafari = true;
    }

    return isSafari;
  }

  constructor(@Inject(IS_SERVER) public isServer: boolean) {}

  get configuration(): EnvironmentConfig {
    return {
      isProd: this.isProd,
      isDev: this.isDev,
      isServer: this.isServer,
      host: this.host,
      apiHost: this.apiHost,
      eloquaApiUrl: this.eloquaApiUrl,
    };
  }

  get currentEnvironmentKey(): string {
    return environment.key;
  }

  get isProd(): boolean {
    return environment.production;
  }

  get isDev(): boolean {
    return !this.isProd;
  }

  get host(): string {
    return environment.host;
  }

  get apiHost(): string {
    return EnvironmentService.apiHost;
  }

  get eloquaApiUrl(): string {
    return environment.eloquaApiUrl;
  }
}
